// ============================================================================
// useQuickSearch — thin hook encapsulating the quick-search pre-fetch logic
//
// Provides `executeQuickSearch(query, maxResults)` which calls the
// `quickSearch` mutation with a 3-second AbortController timeout. On success
// it returns the `data.quickSearch` payload; on any failure (network error,
// GraphQL error, or timeout) it logs at warn level and returns null so the
// caller can fall through to the normal agent flow without interruption.
//
// Requirements: 8.3, 8.4
// ============================================================================

import { useCallback } from 'react';
import { quickSearch } from 'src/utilities/AgentChatAPIs';
import { prewarmAuthToken } from 'src/utilities/AISearchAppSync';

/**
 * Hard timeout (ms) for the quick-search network round-trip.
 *
 * The Cognito token refresh is pre-warmed *before* this timer starts (see
 * `executeQuickSearch`), so this budget covers the AppSync + Lambda round-trip
 * rather than auth setup.
 *
 * Sizing (measured against beta `beta-ateam-quick-search`, Jun 2026): warm
 * Lambda execution is ~0.3–1.4s (p99 ~1.8s) and cold-start init adds ~0.85s, so
 * the Lambda itself peaks near ~2.7s. Retrieval latency is effectively flat
 * across candidate counts (4 / 40 / 100 all landed ~1.5–2s warm), so the
 * post-retrieve App_Resource over-fetch does NOT widen this budget. The extra
 * headroom over the backend ceiling absorbs a cold-path tail — AppSync resolver
 * cold start + network hops on top of a Lambda cold start — which can push the
 * full client round-trip past 5s even though the backend is healthy. A late but
 * valid result is rendered into its card slot rather than discarded (quick
 * search runs concurrently with the agent answer), so erring toward a slightly
 * larger budget is preferable to dropping a good result.
 */
const QUICK_SEARCH_TIMEOUT_MS = 10000;

/**
 * Hook that exposes a single `executeQuickSearch` function for pre-fetching
 * quick search results with a hard timeout.
 *
 * The returned function is wrapped in `useCallback` with an empty dependency
 * array — safe because it only closes over module-level imports. This ensures
 * a stable reference so consumers (e.g. AgentChatProvider) can include it in
 * their own `useCallback` dependency arrays without causing unnecessary
 * re-renders.
 *
 * @returns {{ executeQuickSearch: (query: string, maxResults?: number) => Promise<object|null> }}
 */
const useQuickSearch = () => {
  /**
   * Execute a quick search call with a hard timeout.
   *
   * - On success: returns `data.quickSearch` (results + result_count).
   * - On timeout (>{@link QUICK_SEARCH_TIMEOUT_MS}ms): aborts via AbortSignal,
   *   logs at warn level, returns null.
   * - On failure (network/GraphQL error): logs at warn level, returns null.
   *
   * The Cognito token is pre-warmed before the timeout timer is armed, so the
   * (potentially network-bound) `Auth.currentSession()` refresh does not eat
   * into the timeout budget — the timer covers only the search round-trip. A
   * pre-warm failure is non-fatal: we proceed and let the call itself surface
   * any real auth error through the normal failure path.
   *
   * @param {string} query – the user's search query
   * @param {number} [maxResults] – optional cap on results
   * @returns {Promise<object|null>} quick search payload or null on failure
   */
  const executeQuickSearch = useCallback(async (query, maxResults) => {
    // Pre-warm the auth token OUTSIDE the timeout window so a token refresh
    // can't consume the search budget. Non-fatal on failure.
    try {
      await prewarmAuthToken();
    } catch (error) {
      // Ignore — the quickSearch call below will refetch the token and its
      // failure path handles a genuine auth problem.
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), QUICK_SEARCH_TIMEOUT_MS);
    try {
      const result = await quickSearch(query, maxResults, { signal: controller.signal });
      clearTimeout(timeout);
      return result?.data?.quickSearch ?? null;
    } catch (error) {
      clearTimeout(timeout);
      if (error?.name === 'AbortError') {
        console.warn(`[QuickSearch] timed out after ${QUICK_SEARCH_TIMEOUT_MS}ms`);
      } else if (error?.graphqlErrors) {
        // `quickSearch` attaches the AppSync `errors` payload to
        // `graphqlErrors`. Surface it — the wrapper message alone
        // ("quickSearch returned GraphQL errors") hides the actual cause
        // (auth, resolver/Lambda failure, schema mismatch, etc.).
        console.warn('[QuickSearch] failed:', error.name, error.message, error.graphqlErrors);
      } else {
        console.warn('[QuickSearch] failed:', error?.name, error?.message);
      }
      return null;
    }
  }, []);

  return { executeQuickSearch };
};

export default useQuickSearch;
